import React, { useEffect, useRef, useState } from 'react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function LiveTracking({ deliveryLocation, destinationLocation, restaurantLocation, orderStatus }) {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState({ delivery: null, destination: null, restaurant: null });
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [routeInfo, setRouteInfo] = useState({ distance: null, duration: null });

  // Load Google Maps Script
  useEffect(() => {
    // Check if API key is available
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_API_KEY_HERE') {
      setError('Google Maps API key not configured. Please check your .env file.');
      setIsLoading(false);
      return;
    }

    // Check if script is already loaded AND fully initialized
    if (window.google && window.google.maps && window.google.maps.Map) {
      setIsLoading(false);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Wait for script to fully load
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.Map) {
          clearInterval(checkGoogleMaps);
          setIsLoading(false);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkGoogleMaps);
        if (!window.google || !window.google.maps || !window.google.maps.Map) {
          setError('Google Maps failed to initialize');
          setIsLoading(false);
        }
      }, 10000);
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,places&loading=async`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';

    // Wait for Google Maps to be fully loaded
    script.onload = () => {
      // Poll until google.maps.Map is available
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.Map) {
          clearInterval(checkGoogleMaps);
          setIsLoading(false);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkGoogleMaps);
        if (!window.google || !window.google.maps || !window.google.maps.Map) {
          setError('Google Maps failed to initialize');
          setIsLoading(false);
        }
      }, 10000);
    };

    script.onerror = () => {
      setError('Failed to load Google Maps. Please check your API key and internet connection.');
      setIsLoading(false);
    };
    document.head.appendChild(script);

    // Listen for Google Maps errors
    window.gm_authFailure = () => {
      setError('Google Maps API Error: Please check your API key restrictions in Google Cloud Console. Make sure to add http://localhost:3000 to the allowed referrers.');
      setIsLoading(false);
    };

    return () => {
      // Don't remove script on cleanup to allow reuse
    };
  }, []);

  // Initialize Map
  useEffect(() => {
    // Add delay to ensure DOM is ready
    if (!isLoading && !error && !map && window.google && window.google.maps && window.google.maps.Map) {
      const initMap = () => {
        if (!mapRef.current) {
          console.warn('Map container not ready');
          return;
        }

        try {
          console.log('Initializing Google Map...');
          const newMap = new window.google.maps.Map(mapRef.current, {
            zoom: 13,
            center: destinationLocation || { lat: 0, lng: 0 },
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }]
              }
            ]
          });

          console.log('Map initialized successfully');
          setMap(newMap);

          // Initialize directions renderer
          const renderer = new window.google.maps.DirectionsRenderer({
            map: newMap,
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#4F46E5',
              strokeWeight: 4,
              strokeOpacity: 0.8
            }
          });
          setDirectionsRenderer(renderer);
          console.log('DirectionsRenderer initialized');
        } catch (err) {
          console.error('Map initialization error:', err);
          setError('Failed to initialize map: ' + err.message);
        }
      };

      // Small delay to ensure DOM is rendered
      const timer = setTimeout(initMap, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, error, map, destinationLocation]);

  // Update markers and route
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;

    // Debug logging
    console.log('🗺️ Map Update - Locations:', {
      delivery: deliveryLocation,
      destination: destinationLocation,
      restaurant: restaurantLocation
    });

    // Clear existing markers
    if (markers.delivery) markers.delivery.setMap(null);
    if (markers.destination) markers.destination.setMap(null);
    if (markers.restaurant) markers.restaurant.setMap(null);

    const newMarkers = {};

    // Add destination marker (user's delivery address)
    if (destinationLocation && destinationLocation.lat && destinationLocation.lng) {
      newMarkers.destination = new window.google.maps.Marker({
        position: destinationLocation,
        map: map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#EF4444',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3
        },
        label: {
          text: '📍',
          fontSize: '24px',
          color: '#FFFFFF'
        },
        title: 'Delivery Destination'
      });
    }

    // Add restaurant marker (if location available)
    // Handle both {lat, lng} and {latitude, longitude} formats
    const restaurantPos = restaurantLocation && (
      (restaurantLocation.lat && restaurantLocation.lng)
        ? { lat: restaurantLocation.lat, lng: restaurantLocation.lng }
        : (restaurantLocation.latitude && restaurantLocation.longitude)
          ? { lat: restaurantLocation.latitude, lng: restaurantLocation.longitude }
          : null
    );

    if (restaurantPos) {
      console.log('✅ Creating restaurant marker at:', restaurantPos);
      newMarkers.restaurant = new window.google.maps.Marker({
        position: restaurantPos,
        map: map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#F59E0B',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3
        },
        label: {
          text: '🏪',
          fontSize: '24px',
          color: '#FFFFFF'
        },
        title: 'Restaurant Location'
      });
    } else if (restaurantLocation) {
      console.log('❌ Restaurant location format not recognized:', restaurantLocation);
    }

    // Add delivery partner marker (if location available)
    if (deliveryLocation && deliveryLocation.lat && deliveryLocation.lng) {
      newMarkers.delivery = new window.google.maps.Marker({
        position: deliveryLocation,
        map: map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#10B981',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3
        },
        label: {
          text: '🛵',
          fontSize: '24px',
          color: '#FFFFFF'
        },
        title: 'Delivery Partner',
        animation: window.google.maps.Animation.BOUNCE,
        zIndex: 1000
      });

      // Stop bouncing after 2 seconds
      setTimeout(() => {
        if (newMarkers.delivery) {
          newMarkers.delivery.setAnimation(null);
        }
      }, 2000);

      // Draw route if both locations exist
      if (destinationLocation && destinationLocation.lat && destinationLocation.lng) {
        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(
          {
            origin: deliveryLocation,
            destination: destinationLocation,
            travelMode: window.google.maps.TravelMode.DRIVING
          },
          (result, status) => {
            if (status === 'OK' && directionsRenderer) {
              directionsRenderer.setDirections(result);

              // Extract distance and duration from the route
              const route = result.routes[0];
              if (route && route.legs && route.legs.length > 0) {
                const leg = route.legs[0];
                setRouteInfo({
                  distance: leg.distance.text,
                  duration: leg.duration.text,
                  distanceValue: leg.distance.value, // in meters
                  durationValue: leg.duration.value  // in seconds
                });
              }
            }
          }
        );
      }

      // Fit bounds to show all markers
      const bounds = new window.google.maps.LatLngBounds();
      if (deliveryLocation) bounds.extend(deliveryLocation);
      if (destinationLocation) bounds.extend(destinationLocation);
      if (restaurantPos) bounds.extend(restaurantPos);

      // Add padding to bounds
      const padding = { top: 100, right: 100, bottom: 100, left: 100 };
      map.fitBounds(bounds, padding);
    } else if (destinationLocation || restaurantPos) {
      // Center on available location
      const centerLocation = destinationLocation || restaurantPos;
      map.setCenter(centerLocation);
      map.setZoom(15);
    } else if (deliveryLocation) {
      // Only delivery location available, center on it
      map.setCenter(deliveryLocation);
      map.setZoom(15);
    }

    setMarkers(newMarkers);

    // Cleanup function
    return () => {
      if (newMarkers.delivery) newMarkers.delivery.setMap(null);
      if (newMarkers.destination) newMarkers.destination.setMap(null);
      if (newMarkers.restaurant) newMarkers.restaurant.setMap(null);
    };
  }, [map, deliveryLocation, destinationLocation, restaurantLocation, directionsRenderer]);

  if (error) {
    return (
      <div style={{
        padding: 'var(--space-xl)',
        textAlign: 'center',
        background: 'var(--gray-100)',
        borderRadius: 'var(--radius-lg)',
        color: 'var(--gray-600)'
      }}>
        <div style={{ fontSize: '48px', marginBottom: 'var(--space-md)' }}>⚠️</div>
        <p>{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{
        padding: 'var(--space-xl)',
        textAlign: 'center',
        background: 'var(--gray-100)',
        borderRadius: 'var(--radius-lg)',
        color: 'var(--gray-600)'
      }}>
        <div style={{ fontSize: '48px', marginBottom: 'var(--space-md)' }}>🗺️</div>
        <p>Loading map...</p>
      </div>
    );
  }

  // Check if tracking is available
  const isTrackingActive = ['assigned_delivery', 'picked_up', 'on_the_way'].includes(orderStatus);
  const hasDeliveryLocation = deliveryLocation && deliveryLocation.lat && deliveryLocation.lng;

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '500px',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)'
        }}
      />

      {/* Status indicator */}
      <div style={{
        position: 'absolute',
        top: 'var(--space-md)',
        left: 'var(--space-md)',
        background: 'white',
        padding: 'var(--space-md)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)'
      }}>
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: isTrackingActive && hasDeliveryLocation ? 'var(--success)' : 'var(--gray-400)',
          animation: isTrackingActive && hasDeliveryLocation ? 'pulse 2s infinite' : 'none'
        }} />
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', color: 'var(--gray-900)' }}>
          {isTrackingActive && hasDeliveryLocation ? 'Live Tracking' : 'Tracking Not Available'}
        </span>
      </div>

      {/* ETA and Distance Info */}
      {isTrackingActive && hasDeliveryLocation && routeInfo.distance && (
        <div style={{
          position: 'absolute',
          top: 'var(--space-md)',
          right: 'var(--space-md)',
          background: 'white',
          padding: 'var(--space-md)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          minWidth: '200px'
        }}>
          <div style={{ marginBottom: 'var(--space-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)' }}>
              <span style={{ fontSize: '16px' }}>⏱️</span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', fontWeight: '600' }}>
                ETA
              </span>
            </div>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', color: 'var(--success)' }}>
              {routeInfo.duration}
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)' }}>
              <span style={{ fontSize: '16px' }}>📏</span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', fontWeight: '600' }}>
                DISTANCE
              </span>
            </div>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', color: 'var(--primary)' }}>
              {routeInfo.distance}
            </div>
          </div>
        </div>
      )}

      {/* Map Legend */}
      <div style={{
        position: 'absolute',
        bottom: 'var(--space-md)',
        right: 'var(--space-md)',
        background: 'white',
        padding: 'var(--space-md)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        fontSize: 'var(--font-size-sm)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-xs)' }}>
          <span style={{ fontSize: '20px' }}>🏪</span>
          <span style={{ color: 'var(--gray-700)' }}>Restaurant</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-xs)' }}>
          <span style={{ fontSize: '20px' }}>🛵</span>
          <span style={{ color: 'var(--gray-700)' }}>Delivery Partner</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <span style={{ fontSize: '20px' }}>📍</span>
          <span style={{ color: 'var(--gray-700)' }}>Delivery Destination</span>
        </div>
      </div>

      {/* No tracking message */}
      {!isTrackingActive && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: 'var(--space-xl)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-xl)',
          textAlign: 'center',
          maxWidth: '300px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: 'var(--space-md)' }}>🚴</div>
          <h3 style={{ marginBottom: 'var(--space-sm)', color: 'var(--gray-900)' }}>
            Delivery Partner Not Assigned Yet
          </h3>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', margin: 0 }}>
            Live tracking will be available once a delivery partner is assigned to your order.
          </p>
        </div>
      )}
    </div>
  );
}

export default LiveTracking;
